import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Trash2, CheckCircle2, ShoppingBag } from "lucide-react";
import { API } from "@/constants";
import { useBasket } from "@/context/BasketContext";
import { formatApiErrorDetail } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmartImage from "@/components/SmartImage";

const inputCls =
  "w-full rounded-lg border border-kera-line bg-white px-4 py-2.5 text-sm text-kera-ink outline-none focus:border-kera-gold";

export default function BasketPage() {
  const { items, remove, clear } = useBasket();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        items: items.map((i) => ({ id: i.id, name: i.name })),
      });
      setSent(true);
      clear();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-white text-kera-ink font-body min-h-screen" data-testid="basket-page">
      <Navbar />
      <div className="px-4 sm:px-8 lg:px-16 max-w-5xl mx-auto pt-28 pb-20">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-kera-copper mb-4">Wholesale &amp; Stockist</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-kera-ink" data-testid="basket-title">
          Enquiry List
        </h1>
        <p className="mt-4 max-w-xl text-base text-kera-muted leading-relaxed">
          Review your selected products and send one wholesale request — we reply with pricing, pack availability and delivery for your region.
        </p>

        {sent ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-14 rounded-2xl border border-kera-gold/50 bg-[#FAF7F0] p-10 text-center"
            data-testid="basket-success"
          >
            <CheckCircle2 className="mx-auto text-kera-copper" size={40} strokeWidth={1.5} />
            <h2 className="mt-5 font-display font-semibold text-2xl text-kera-ink">Enquiry sent</h2>
            <p className="mt-3 text-sm text-kera-muted max-w-md mx-auto leading-relaxed">
              Thank you — the KERA team will come back to you with wholesale pricing and availability shortly.
            </p>
            <Link to="/products" className="mt-7 inline-block rounded-full bg-black text-white px-7 py-3 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="basket-success-continue">
              Continue browsing
            </Link>
          </motion.div>
        ) : items.length === 0 ? (
          <div className="mt-14 text-center py-16 border border-dashed border-kera-line rounded-2xl" data-testid="basket-empty">
            <ShoppingBag className="mx-auto text-kera-line" size={40} strokeWidth={1.2} />
            <p className="mt-5 font-display font-semibold text-xl text-kera-ink">Your enquiry list is empty</p>
            <p className="mt-2 text-sm text-kera-muted">Browse the range and add the products you'd like to stock.</p>
            <Link to="/products" className="mt-7 inline-block rounded-full bg-black text-white px-7 py-3 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="basket-browse">
              Browse products
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2" data-testid="basket-items">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-4">
                {items.length} product{items.length === 1 ? "" : "s"} selected
              </p>
              <ul className="space-y-4">
                {items.map((item) => (
                  <li key={item.id} className="flex items-center gap-4 rounded-xl border border-kera-line bg-white p-3" data-testid={`basket-item-${item.id}`}>
                    <SmartImage src={item.image} alt={item.name} sizes="64px" className="h-16 w-16 rounded-lg object-cover border border-kera-line" />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-kera-copper">{item.category}</p>
                      <Link to={`/products/${item.id}`} className="font-display font-semibold text-kera-ink hover:text-kera-copper transition-colors">
                        KERA {item.name}
                      </Link>
                    </div>
                    <button onClick={() => remove(item.id)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" aria-label={`Remove ${item.name}`} data-testid={`basket-remove-${item.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-kera-line bg-white p-6 sm:p-8 h-fit" data-testid="basket-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">Name *</label>
                  <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="basket-field-name" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">Company</label>
                  <input value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="basket-field-company" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="basket-field-email" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">Phone</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="basket-field-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">Message</label>
                  <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Quantities, delivery region, timelines…" className={inputCls} data-testid="basket-field-message" />
                </div>
              </div>
              {error && <p className="mt-4 text-sm text-red-700" data-testid="basket-error">{error}</p>}
              <button
                type="submit"
                disabled={sending}
                className="mt-6 w-full rounded-full bg-black text-white py-3.5 text-sm font-semibold tracking-wide hover:bg-kera-gold hover:text-black transition-colors duration-300 disabled:opacity-50"
                data-testid="basket-submit"
              >
                {sending ? "Sending…" : `Send Wholesale Enquiry · ${items.length} item${items.length === 1 ? "" : "s"}`}
              </button>
            </form>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
